// src/ChooseYourPlan.jsx (or your components directory)
import React, { useState } from "react";

// Checkmark SVG Icon
const CheckIcon = () => (
  <svg
    className="w-5 h-5 text-green-600 mr-2 flex-shrink-0"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M5 13l4 4L19 7"
    ></path>
  </svg>
);

const PricingCard = ({ plan, billingCycle }) => {
  const isYearly = billingCycle === "yearly";
  const price = isYearly ? plan.priceYearly : plan.priceMonthly;
  const periodText = isYearly ? "/Year" : "/Month";

  return (
    <div
      className={`bg-white p-6 md:p-8 rounded-2xl shadow-lg flex flex-col h-full ${
        plan.isPopular ? "border-2 border-pink-500" : "border border-gray-200"
      }`}
    >
      <div className="flex justify-between items-start mb-4">
        <span
          className={`text-sm font-semibold px-3 py-1 rounded-full ${plan.badgeStyle}`}
        >
          {plan.name}
        </span>
        {plan.isPopular && (
          <span className="text-xs font-semibold text-pink-600 bg-pink-100 px-3 py-1 rounded-full">
            Most Popular
          </span>
        )}
      </div>

      <p className="text-gray-600 text-sm mb-4 min-h-[3em]">
        {plan.description}
      </p>

      <div className="mb-6">
        <span className="text-4xl md:text-5xl font-bold text-gray-900">
          {plan.currency}
          {price}
        </span>
        <span className="text-gray-500 text-sm ml-1">{periodText}</span>
      </div>

      <hr className="my-6 border-gray-200" />

      <div className="mb-8 flex-grow">
        <p className="font-semibold text-gray-700 mb-3">
          {plan.featureHeader || "Features"}
        </p>
        <ul className="space-y-3">
          {plan.features.map((feature, index) => (
            <li key={index} className="flex items-start text-sm text-gray-600">
              <CheckIcon />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <button
        className={`w-full py-3 px-6 rounded-lg font-semibold text-center transition-colors
          ${
            plan.buttonType === "outline"
              ? "bg-white text-green-800 border-2 border-green-800 hover:bg-green-50"
              : "bg-green-800 text-white hover:bg-green-700"
          }`}
      >
        {plan.buttonText}
      </button>
    </div>
  );
};

const ChooseYourPlan = () => {
  const [billingCycle, setBillingCycle] = useState("yearly"); // 'monthly' or 'yearly'

  const plansData = [
    {
      name: "Free",
      badgeStyle: "bg-orange-100 text-[#E55812]",
      description:
        "Start strong with smart job matches and Resume building tools.",
      priceMonthly: 0,
      priceYearly: 0,
      currency: "$",
      features: [
        "Career path recommendations",
        "Job suggestions",
        "Resume building (no download)",
      ],
      isPopular: false,
      buttonText: "Choose Plan",
      buttonType: "outline",
    },
    {
      name: "Pro",
      badgeStyle: "bg-blue-100 text-blue-700",
      description:
        "Unlock tailored Resume downloads and direct applications with precision tools.",
      priceMonthly: 3.99,
      priceYearly: 25,
      currency: "$",
      featureHeader: "Everything in Free, plus:",
      features: [
        "Download tailored Resumes",
        "Direct job application access",
        "Advanced formatting and keyword optimization",
      ],
      isPopular: true,
      buttonText: "Choose Plan",
      buttonType: "solid",
    },
  ];

  return (
    // Added font-poppins class here
    <section id="pricing" className="py-16 lg:py-24 bg-white font-poppins">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Combined Section Header and Billing Toggle */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start mb-10 lg:mb-12">
          {" "}
          {/* Changed to lg:items-start */}
          {/* Left Side: Title and Subtitle */}
          <div className="text-center lg:text-left mb-6 lg:mb-0">
            <p className="text-sm font-semibold text-[#E55812] uppercase tracking-wider mb-1">
              Pricing
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
              Choose your plan
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto lg:mx-0 text-base md:text-lg">
              Explore Our Plans for AI-Powered Career Tools That Fit Your Goals
              and Accelerate Your Job Search Today!
            </p>
          </div>
          {/* Right Side: Billing Cycle Toggle */}
          {/* Apply a negative top margin on large screens to pull the toggle up */}
          <div className="flex justify-center lg:justify-end w-full lg:w-auto lg:-mt-2">
            {" "}
            {/* Example: lg:-mt-2. Adjust as needed. */}
            <div className="bg-gray-100 p-1 rounded-lg flex space-x-1">
              <button
                onClick={() => setBillingCycle("monthly")}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-colors
                  ${
                    billingCycle === "monthly"
                      ? "bg-green-800 text-white shadow-sm"
                      : "text-gray-700 hover:bg-gray-200"
                  }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle("yearly")}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-colors
                  ${
                    billingCycle === "yearly"
                      ? "bg-green-800 text-white shadow-sm"
                      : "text-gray-700 hover:bg-gray-200"
                  }`}
              >
                Yearly
              </button>
            </div>
          </div>
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plansData.map((plan) => (
            <PricingCard
              key={plan.name}
              plan={plan}
              billingCycle={billingCycle}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ChooseYourPlan;
